import { useState } from "react";
import { useTranslation } from 'react-i18next';

const anemiaIndicators = ["koilonychia", "pale-nail", "beau-lines", "leukonychia", "red-lunula"];

interface ScoreResult {
  score: number;
  interpretation: string;
  resultCategory: string;
  resultIconType: 'smile' | 'meh' | 'frown';
  resultColor: string;
}

export function useAnemiaScore() {
  const [score, setScore] = useState<number | null>(null);
  const [interpretation, setInterpretation] = useState("");
  const { t } = useTranslation();

  const calculateScore = (eyesResult: any, nailsResult: any): ScoreResult => {
    // Ojos: Si no hay predicciones, asumimos que no detectó anemia (eyeFactor = 1)
    const eyesConfidence = eyesResult?.predictions?.length > 0
      ? eyesResult.predictions.reduce((max, pred) => (pred.confidence > max ? pred.confidence : max), 0)
      : 0;
    const eyeFactor = eyesResult?.predictions?.length > 0 ? (1 - eyesConfidence) : 1;

    // Uñas: solo si las predicciones están dentro de las clases anemiaIndicators
    const relevantNailPredictions = nailsResult?.predictions?.filter(pred =>
      anemiaIndicators.includes(pred.class.toLowerCase())
    );

    const nailsConfidenceMax = relevantNailPredictions?.length > 0
      ? relevantNailPredictions.reduce((max, curr) => (curr.confidence > max ? curr.confidence : max), 0)
      : 0;

    const nailFactor = relevantNailPredictions?.length > 0 ? (1 - nailsConfidenceMax) : 1;

    // Si ambos no detectan anemia, score automático 100
    const finalScore = eyeFactor === 1 && nailFactor === 1
      ? 100
      : Math.round(((eyeFactor + nailFactor) / 2) * 100);

    setScore(finalScore);

    let resultCategory = '';
    let resultIconType: 'smile' | 'meh' | 'frown' = 'meh';
    let resultColor = '';
    let interpretationText = '';

    if (finalScore > 85) {
      resultCategory = t('results.healthy');
      resultIconType = 'smile';
      resultColor = 'fuerte';
      interpretationText = t('results.messages.healthy');
    } else if (finalScore > 60) {
      resultCategory = t('results.lowRisk');
      resultIconType = 'meh';
      resultColor = 'warning';
      interpretationText = t('results.messages.moderate');
    } else if (finalScore > 30) {
      resultCategory = t('results.moderateRisk');
      resultIconType = 'meh';
      resultColor = 'warning';
      interpretationText = t('results.messages.high');
    } else {
      resultCategory = t('results.highRisk');
      resultIconType = 'frown';
      resultColor = 'sangre';
      interpretationText = t('results.messages.high');
    }

    setInterpretation(interpretationText);

    return {
      score: finalScore,
      interpretation: interpretationText,
      resultCategory,
      resultIconType,
      resultColor
    };
  };

  return { score, interpretation, calculateScore };
}
